// Turrets (§31 S8, design "TURRETS"): the guns' geometry, their round, their
// appetite. Nothing here does damage — `rules/gunfire.ts` fires, through
// `damage`, which is a cycle from here.
//
// Three kinds, one row each in `TURRET_GUNS`: the Laser (kind 1, the old
// generic turret's id), Kinetic (22), Cannon (23). A turret's round runs from
// its owner's turn start (`refreshTurrets`: shots refill, banked fuel
// expires) to the next; it fires at its owner's End Turn and what it did not
// spend is overwatch, fired at enemies entering its field on their turns.
//
// **`attacks.ts` never sees a turret** — the Unicorn ruling: reach that only
// hurts goes through `damage`, which refuses a last royal, so it never checks
// and perft cannot see it.

import { type Color, COORD_MAX, COORD_MIN, EAST, fileOf, forwardFacing, NORTH, pieceColor, rankOf, SOUTH, square, WEST } from "../board";
import { areEnemies, hasCell, pieceAt, type Position } from "../position";
import { canBeHurt } from "../chess/moveGen";
import { clearLine } from "../chess/plow";
import { type GunRow, inventoryOf, TURRET_GUNS } from "../rules";
import { CANNON_TURRET, KINETIC_TURRET, type Structure, type StructureKind, TURRET, type TurretState, unitMeets, unitValue } from "./state";

/** The three turret kinds, in kind order. */
export const TURRET_KINDS: readonly StructureKind[] = [TURRET, KINETIC_TURRET, CANNON_TURRET];

export const isTurretKind = (kind: number): boolean => TURRET_GUNS[kind] !== undefined;

/** The gun on a turret kind. */
export const gunOf = (kind: number): GunRow => TURRET_GUNS[kind] ?? TURRET_GUNS[TURRET];

/** Targeting Arrays past the first — what scales every gun (§31 A7, S8). */
export const extraArrays = (arrays: number): number => Math.max(0, arrays - 1);

/** Shots a round, with `arrays` powered Targeting Arrays. */
export const shotsFor = (kind: number, arrays: number): number =>
  gunOf(kind).shots + gunOf(kind).arrayShots * extraArrays(arrays);

/** The field's reach: a radius, or a cone's depth (+`arrayDepth` a spare array). */
export const reachFor = (kind: number, arrays: number): number =>
  gunOf(kind).reach + gunOf(kind).arrayDepth * extraArrays(arrays);

/** A turret's state the moment it stands: a full round, no fuel, facing its side's forward. */
export function newTurret(kind: number, owner: Color, arrays = 1): TurretState {
  return { shots: shotsFor(kind, arrays), fuel: 0, facing: forwardFacing(owner) };
}

/** Did it go up over an own Reinforced Wall? */
export const turretEmplaced = (structure: Structure): boolean => structure.turret?.emplaced === true;

const away = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/** The step to the left of a facing, as (file, rank) — what "left" means in a sweep. */
const LEFT: Record<number, readonly [number, number]> = {
  [NORTH]: [-1, 0],
  [EAST]: [0, 1],
  [SOUTH]: [1, 0],
  [WEST]: [0, -1]
};
const FORWARD: Record<number, readonly [number, number]> = {
  [NORTH]: [0, 1],
  [EAST]: [1, 0],
  [SOUTH]: [0, -1],
  [WEST]: [-1, 0]
};

/**
 * The cone's squares in **sweep order**: layer 1 out to `depth`, layer k
 * `2k−1` wide; odd layers left→right, even right→left (seen down the
 * facing). Off the coordinate range dropped; cells and sight are the
 * caller's.
 */
export function coneSquares(at: number, facing: number, depth: number): number[][] {
  const [ff, fr] = FORWARD[facing] ?? FORWARD[NORTH];
  const [lf, lr] = LEFT[facing] ?? LEFT[NORTH];
  const layers: number[][] = [];
  for (let k = 1; k <= depth; k++) {
    const row: number[] = [];
    for (let o = -(k - 1); o <= k - 1; o++) {
      // o < 0 is left: left→right is o from −(k−1) up.
      const file = fileOf(at) + ff * k - lf * o;
      const rank = rankOf(at) + fr * k - lr * o;
      if (file < COORD_MIN || rank < COORD_MIN || file > COORD_MAX || rank > COORD_MAX) continue;
      row.push(square(file, rank));
    }
    layers.push(k % 2 === 1 ? row : row.reverse());
  }
  return layers;
}

/**
 * A turret's **field**: every square it can shoot, on a cell and in sight
 * (`clearLine` — an obstruction between blocks it, §31 S1), in its firing
 * order's layers. A radius is one layer by Chebyshev distance, nearest first.
 */
export function fieldLayers(position: Position, at: number, structure: Structure, arrays: number): number[][] {
  const kind = structure.kind;
  const gun = gunOf(kind);
  const reach = reachFor(kind, arrays);
  const seen = (sq: number): boolean => sq !== at && hasCell(position, sq) && clearLine(position, at, sq);
  if (gun.shape === "cone") {
    const layers = coneSquares(at, structure.turret?.facing ?? forwardFacing(structure.owner), reach).map((row) =>
      row.filter(seen)
    );
    return gun.order === "far" ? layers.reverse() : layers;
  }
  const out: number[] = [];
  for (const sq of position.cellList) if (away(sq, at) <= reach && seen(sq)) out.push(sq);
  out.sort((a, b) => away(a, at) - away(b, at) || a - b);
  return [out];
}

/** Every square of the field, flat. */
export const fieldSquares = (position: Position, at: number, structure: Structure, arrays: number): number[] =>
  fieldLayers(position, at, structure, arrays).flat();

/** Is `sq` in the turret's field? */
export function inField(position: Position, at: number, structure: Structure, arrays: number, sq: number): boolean {
  const kind = structure.kind;
  const gun = gunOf(kind);
  const reach = reachFor(kind, arrays);
  if (sq === at || !hasCell(position, sq)) return false;
  if (gun.shape === "radius") return away(sq, at) <= reach && clearLine(position, at, sq);
  return fieldSquares(position, at, structure, arrays).includes(sq);
}

/** Can the piece on `sq` be shot by a turret of `owner`'s — an enemy, and hurtable? */
export function shootable(position: Position, owner: Color, sq: number): boolean {
  const piece = pieceAt(position, sq);
  return piece !== 0 && areEnemies(position, owner, pieceColor(piece)) && canBeHurt(position, sq, piece);
}

/**
 * Who the turret on `at` would fire at, in its order — every shootable enemy
 * in the field, once. A Laser's order is the caller's draw (`random`); this
 * gives it in square order.
 */
export function turretTargets(position: Position, at: number, structure: Structure, arrays: number): number[] {
  return fieldSquares(position, at, structure, arrays).filter((sq) => shootable(position, structure.owner, sq));
}

/** Ammunition in its stock (§31 S8): marble for a Kinetic, steel or better for a Cannon. */
export const ammoOf = (structure: Structure): number => {
  const ammo = gunOf(structure.kind).ammo;
  if (!ammo) return 0;
  return structure.stock.filter((unit) => unitMeets(unit, ammo.line as 0, ammo.grade)).length;
};

/** Does this turret take `unit` as ammunition at all? Capability, never occupancy. */
export function turretTakes(structure: Structure, unit: number): boolean {
  const ammo = gunOf(structure.kind).ammo;
  return ammo !== undefined && unitMeets(unit, ammo.line as 0, ammo.grade);
}

/** Room in the magazine: the turret's inventory (§33 I2). */
export const magazineRoom = (structure: Structure): number =>
  gunOf(structure.kind).ammo ? Math.max(0, inventoryOf(structure.kind) - structure.stock.length) : 0;

/**
 * Fuel points it still wants banked this round (§31 S8): a Laser one per
 * shot left; a Kinetic or Cannon one for the round.
 */
export function turretFuelWant(structure: Structure): number {
  const state = structure.turret;
  if (!state || !isTurretKind(structure.kind)) return 0;
  const need = gunOf(structure.kind).fuelPerShot ? state.shots : state.shots > 0 ? 1 : 0;
  return Math.max(0, need - state.fuel);
}

/** May it take one shot now: a shot left, its fuel, its round? */
export function canShoot(structure: Structure): boolean {
  const state = structure.turret;
  // Negated so a pre-S8 save's stats (no `shots`, no `fuel`) read as empty.
  if (!state || !(state.shots > 0)) return false;
  if (!(state.fuel >= 1)) return false;
  return !gunOf(structure.kind).ammo || ammoOf(structure) > 0;
}

/** Spend one shot: the shot, a Laser's fuel point, one round of ammunition (cheapest). */
export function payShot(structure: Structure): void {
  const state = structure.turret!;
  state.shots--;
  const gun = gunOf(structure.kind);
  if (gun.fuelPerShot) state.fuel--;
  const ammo = gun.ammo;
  if (!ammo) return;
  let best = -1;
  for (let i = 0; i < structure.stock.length; i++) {
    const unit = structure.stock[i];
    if (!unitMeets(unit, ammo.line as 0, ammo.grade)) continue;
    if (best < 0 || unitValue(unit) < unitValue(structure.stock[best])) best = i;
  }
  if (best >= 0) structure.stock.splice(best, 1);
}

